/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Flex,
  Text,
  IconButton,
  useColorModeValue,
  useDisclosure,
  HStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Center,
  Circle,
  Button
} from '@chakra-ui/react';
import { HamburgerIcon, CloseIcon, ChevronDownIcon } from '@chakra-ui/icons';
import {
  QAIcon,
  HelpIcon,
  LogoutIcon,
  UserIcon,
  WhiteBellIcon,
} from '../../constants/IconData';
import AppColors from '../../constants/AppColors';
import { AuthContext } from '../../providers/AuthProvider';
import React, { useContext, useEffect, useMemo } from 'react';
import { FONT_FAMILY } from '../../constants/Theme';
import ROUTES from '../../constants/Routes';
import { useNavigate } from 'react-router-dom';
import { replaceRoute, toTitleCase } from '../../utils/common.utils';
import { withProvider } from '../../hoc/withProvider';
import NotificationProvider, {
  NotificationsContext,
} from '../../providers/NotificationProvider';
import { intlFormatDistance } from 'date-fns';
import { useInterval } from '../../hooks/useInterval';
import { ACCESS_TOKEN } from '../../constants/PreferenceKeys';

const PORTAL_VIEWS = {
  ADMIN: [
    { id: process.env.REACT_APP_MANAGER_REDIRECT_URL, name: 'MANAGER' },
    { id: process.env.REACT_APP_AUDITOR_REDIRECT_URL, name: 'AUDITOR' },
  ],
  REVIEWER: [{ id: process.env.REACT_APP_AUDITOR_REDIRECT_URL, name: 'AUDITOR' }],
};


const ViewOptionBtn = ({
  buttonMsg = 'View',
  emptyOptionsMsg = 'No Data Available',
  options = null,
}) => {
  const handleSelectOption = (optionUrl) => {
    if (optionUrl) {
      const url = `${optionUrl}?accessToken=${localStorage.getItem(ACCESS_TOKEN)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  if (!options) {
    return 'Loading...';
  }

  return (
    <Menu>
      <MenuButton
        as={Button}
        rightIcon={<ChevronDownIcon />}
        variant='outline'
        borderColor={AppColors.secondary}
        color={AppColors.secondary}
        _hover={{ bg: AppColors.secondary, color: AppColors.white }}
        _active={{ bg: AppColors.secondary, color: AppColors.white }}
      >
        {buttonMsg}
      </MenuButton>
      <MenuList boxShadow='none'>
        {options.length ? (
          options.map(({ id, name }) => (
            <MenuItem key={id} onClick={() => handleSelectOption(id)}>
              {name}
            </MenuItem>
          ))
        ) : (
          <MenuItem isDisabled>{emptyOptionsMsg}</MenuItem>
        )}
      </MenuList>
    </Menu>
  );
};

function CQTopbar() {
  const navigate = useNavigate();

  const { isOpen, onToggle } = useDisclosure();
  const authContext = useContext(AuthContext);

  const notificationContext = useContext(NotificationsContext);
  const { unReadNotification, setUnReadNotification } = notificationContext;

  const [newNotifications, setNewNotification] = React.useState([]);
  const IS_ADMIN_OR_REVIEWER = useMemo(() => authContext?.currentUser?.role === 'ADMIN' || authContext?.currentUser?.role === 'REVIEWER', [authContext?.currentUser]);

  useEffect(() => {
    notificationContext.getNotifications(1).then(response => {
      if (response?.results) {
        const sorted = [...response.results].sort((a, b) => Number(a.read) - Number(b.read));
        setNewNotification(sorted);
        setUnReadNotification(response.unread_count ?? 0);
      }
    });
  }, []);

  const handleNotificationStatus = async n => {
    if (!n.read) await notificationContext.changeNotificationStatus(n.notification_user_id);
    if (n?.reference_chart) {
      navigate(replaceRoute(ROUTES.VIEW_AUDIT_SHEET, { uploadId: n.reference_chart }));
    }
  };

  const handleMarkSingleRead = async (e, n) => {
    e.stopPropagation();
    if (!n.read) await notificationContext.changeNotificationStatus(n.notification_user_id);
    setNewNotification(prev =>
      prev.filter(notif => notif.notification_user_id !== n.notification_user_id)
    );
  };

  const handleMarkAllRead = async () => {
    await notificationContext.markAllRead();
    setNewNotification(prev => prev.map(n => ({ ...n, read: true })));
  };

  const loadNotification = async () => {
    const response = await notificationContext.loadNewNotification();
    if (!response) return;
    const sorted = [...(response.results ?? [])].sort(
      (a, b) => Number(a.read) - Number(b.read)
    );
    setNewNotification(sorted);
    setUnReadNotification(response.unread_count ?? 0);
  };

  useInterval(loadNotification, 5000);

  return (
    <Box w={'100%'}>
      <Flex
        bg={'#fff'}
        color={useColorModeValue('gray.600', 'white')}
        minH={'60px'}
        borderBottom={1}
        borderStyle={'solid'}
        borderColor={useColorModeValue('gray.200', 'gray.900')}
        align={'center'}
      >
        <Flex
          flex={{ base: 1, md: 'auto' }}
          ml={{ base: -2 }}
          display={{ base: 'flex', md: 'none' }}
        >
          <IconButton
            onClick={onToggle}
            icon={
              isOpen ? <CloseIcon w={3} h={3} /> : <HamburgerIcon w={5} h={5} />
            }
            variant={'ghost'}
            aria-label={'Toggle Navigation'}
          />
        </Flex>
        <Flex flex={{ base: 1 }} ml={6} alignItems={'center'} height={'80px'}>
          <QAIcon
            onClick={() => navigate(ROUTES.DASHBOARD_V2)}
            style={{ cursor: 'pointer', height: '3rem' }}
          />
          <Box
            ml={5}
            borderRadius={'md'}
            height={'60%'}
            opacity={0.5}
            border={'1px solid #fff '}
            mr={2}
          />
          <Text letterSpacing={"1px"} color={AppColors.secondary} fontWeight={"bold"} fontSize={"1.5rem"} fontFamily={FONT_FAMILY.baiJamurjee}>QA Portal</Text>
        </Flex>

        <HStack
          // flex={{ base: 1, md: 0 }}
          alignItems={'center'}
          spacing={2}
          mr={5}
        >
          {IS_ADMIN_OR_REVIEWER && (
            <ViewOptionBtn
              buttonMsg='Open Portal'
              emptyOptionsMsg='No portals available'
              options={PORTAL_VIEWS[authContext?.currentUser?.role]} />
          )}
          <Menu autoSelect={false}>
            <MenuButton position={'relative'}>
              <WhiteBellIcon
                style={{
                  width: 40,
                  height: 40,
                  cursor: 'pointer',
                  display: 'inline-block',
                }}
              />
              {unReadNotification > 0 ? (
                <Circle
                  position={'absolute'}
                  top={0}
                  right={-2}
                  color={AppColors.white}
                  size="20px"
                  bg={AppColors.danger}
                  fontWeight="bold"
                  fontSize={'xs'}
                >
                  {unReadNotification}
                </Circle>
              ) : (
                ''
              )}
            </MenuButton>
            <MenuList shadow={'none'} border={'none'} bg={'transparent'}>
              {newNotifications?.filter(n => !n.read).length === 0 ? (
                <MenuItem borderTopRadius={'md'} bg={AppColors.white} _hover={{ bg: AppColors.white }}>
                  <Box p={4}>No Notifications</Box>
                </MenuItem>
              ) : (
                <>
                  <Box
                    bg={AppColors.white} px={4} py={2} borderTopRadius={'md'}
                    borderBottom={`1px solid ${AppColors.gray}`}
                    display={'flex'} justifyContent={'flex-end'}
                  >
                    <Text
                      fontSize={'xs'} color={AppColors.secondary} cursor={'pointer'}
                      fontWeight={'semibold'} _hover={{ textDecoration: 'underline' }}
                      onClick={handleMarkAllRead}
                    >
                      Mark all as read
                    </Text>
                  </Box>
                  {newNotifications.filter(n => !n.read).slice(0, 3).map((n, i) => (
                    <Box
                      key={`${i}-notification`}
                      display={'flex'}
                      bg={AppColors.white}
                      borderBottom={`1px solid ${AppColors.gray}`}
                    >
                      <Box
                        flex={1} cursor={'pointer'} px={4} py={2} overflow={'hidden'}
                        onClick={() => handleNotificationStatus(n)}
                      >
                        <HStack spacing={1} overflow={'hidden'} mb={'2px'}>
                          {n.message.startsWith('@') && (
                            <Text fontWeight={'semibold'} fontSize={'sm'} color={AppColors.secondary} whiteSpace={'nowrap'}>
                              {toTitleCase(`${n?.sender?.first_name} ${n?.sender?.last_name}`)}
                            </Text>
                          )}
                          <Text fontSize={'sm'} color={'black'} noOfLines={1}>
                            {n.message.startsWith('@') ? n.message?.substring(1) : n.message}
                          </Text>
                        </HStack>
                        <Text color={AppColors.darkGray} fontSize={'11px'}>
                          {intlFormatDistance(new Date(n.created_at), new Date(), { addSuffix: true })}
                        </Text>
                      </Box>
                      <Box
                        w={'44px'} minW={'44px'}
                        display={'flex'} alignItems={'center'} justifyContent={'center'}
                        borderLeft={`1px solid ${AppColors.gray}`}
                        cursor={'pointer'} _hover={{ bg: 'gray.100' }}
                        onClick={e => handleMarkSingleRead(e, n)}
                      >
                        <CloseIcon w={2} h={2} color={AppColors.darkGray} />
                      </Box>
                    </Box>
                  ))}
                </>
              )}
              <MenuItem
                _hover={{ bg: AppColors.secondary }} bg={AppColors.secondary}
                py={3} color={AppColors.white} borderBottomRadius={'md'}
                display={'flex'} justifyContent={'center'}
                onClick={() => navigate(ROUTES.NOTIFICATIONS)}
              >
                <Center fontSize={'sm'}>View all</Center>
              </MenuItem>
            </MenuList>
          </Menu>

          <Menu>
            <MenuButton px={4} py={2} transition="all 0.2s" borderRadius="md">
              <HStack alignItems={'center'}>
                <Text
                  fontWeight={'bold'}
                  color={AppColors.secondary}
                  fontSize={'18px'}
                  fontFamily={FONT_FAMILY.ptSans}
                >
                  {authContext?.currentUser?.email ?? 'N/A'}
                </Text>
                <ChevronDownIcon color={AppColors.secondary} />
              </HStack>
            </MenuButton>
            <MenuList padding={0}>
              <MenuItem
                onClick={() => navigate(ROUTES.ACCOUNT)}
                icon={<UserIcon style={{ width: 15, height: 15 }} />}
                height={'50px'}
                fontSize={'16px'}
              >
                {' '}
                My Account
              </MenuItem>
              {/* <MenuItem
                onClick={() => {
                  navigate(ROUTES.HELP);
                }}
                icon={<HelpIcon style={{ width: 15, height: 15 }} />}
                height={'50px'}
                fontSize={'16px'}
              >
                Help
              </MenuItem> */}
              <MenuItem
                onClick={authContext.onLogout}
                icon={<LogoutIcon style={{ width: 15, height: 15 }} />}
                height={'50px'}
                fontSize={'16px'}
              >
                {' '}
                Logout
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Flex>
    </Box>
  );
}

export default withProvider(NotificationProvider, CQTopbar);
